import { useEffect, useState } from 'react';
import { Head, linkedDrawers, useAdmin } from '../ui/Shell';
import { customerKey } from './CustomerDrawer';
import { Block, Cell, Grid, Kpis } from '../ui/Block';
import { hhmmss, useData } from '../lib/hooks';
import { BillingEvents } from './BillingEvents';
import { BillingPlans } from './BillingPlans';
import { BillingPromoCodes } from './BillingPromoCodes';
import { BillingSettings } from './BillingSettings';
import { errOf, usd, type BillingOverview, type PlanRow } from './billingShared';

type TabKey = 'plans' | 'promo-codes' | 'settings' | 'events';
const TABS: { key: TabKey; label: string }[] = [{ key: 'plans', label: 'Plans' }, { key: 'promo-codes', label: 'Promo codes' }, { key: 'settings', label: 'Settings' }, { key: 'events', label: 'Events' }];
const isTab = (k?: string): k is TabKey => TABS.some((t) => t.key === k);
const linkedTab = (): TabKey | null => { const k = linkedDrawers()[0] ?? ''; return k.startsWith('billing:promo:') ? 'promo-codes' : null; };

export function Billing() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [tab, setTab] = useState<TabKey>(() => isTab(ctx.params.tab) ? ctx.params.tab : linkedTab() ?? 'plans');
  const ov = useData<BillingOverview>(env, '/admin/billing/overview', 30, paused);
  const plans = useData<{ plans: PlanRow[] }>(env, '/admin/billing/plans', 60, paused);
  const o = ov.data;
  const planName = (id: string) => plans.data?.plans.find((p) => p.plan.id === id)?.plan.name ?? id;

  const account = ctx.params.account;
  useEffect(() => { if (account) ctx.open('customers', customerKey(account, 'billing')); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const subs = o?.subscriptionsByPlan ?? [];
  return (
    <>
      <Head title="Billing" sub={<>Plans and their versions, promo codes, the settings row, the provider's webhooks. Each account's credit and charges are on its customer's Billing tab.{o && o.mode !== 'polar' && o.mode !== 'fake' ? <> Billing mode is <span className="mono">{o.mode}</span>: nothing is enforced or metered on this environment.</> : o ? <> Provider: <span className="mono">{o.mode}</span>.</> : ''}</>} />
      <Grid>
        <Cell span={12}>
          <Block title="Overview" asOf={hhmmss(ov.at)} stale={ov.ageS > 90} loading={ov.loading && !o} error={errOf(ov)}>
            {o && <Kpis min={150} items={[
              { label: 'Liability', v: usd(o.liability), sub: 'positive balances outstanding' },
              { label: 'Granted this month', v: usd(o.creditGrantedThisMonth), sub: 'credit entries, calendar month' },
              { label: 'Spent this month', v: usd(o.creditSpentThisMonth), sub: `charges settled · ${usd(o.openCharges)} still open` },
              { label: 'Subscriptions', v: subs.reduce((n, s) => n + s.count, 0).toLocaleString(), sub: subs.length ? subs.map((s) => `${s.count} ${planName(s.planId)}`).join(' · ') : 'none active', onClick: () => ctx.go('customers', { state: 'subscribed' }) },
              { label: 'Accounts', v: o.accounts.toLocaleString(), sub: (o.accountsByPlan ?? []).map((s) => `${s.count} ${planName(s.planId)}`).join(' · ') || undefined, onClick: () => ctx.go('customers') },
              { label: 'In debt', v: o.accountsInDebt.toLocaleString(), sub: `${o.pendingChanges} pending plan change${o.pendingChanges === 1 ? '' : 's'}`, fg: o.accountsInDebt ? 'var(--conf-low)' : undefined, dot: o.accountsInDebt ? 'bad' : 'ok', onClick: () => ctx.go('customers', { state: 'debt' }) },
              { label: 'Auto top-ups failing', v: o.autoTopUpFailing.toLocaleString(), sub: 'enabled with a failure', fg: o.autoTopUpFailing ? 'var(--conf-mid)' : undefined, dot: o.autoTopUpFailing ? 'warn' : 'ok' },
              { label: 'Failed webhooks', v: o.webhookFailures.toLocaleString(), sub: `${o.emailsQueued} email${o.emailsQueued === 1 ? '' : 's'} queued`, fg: o.webhookFailures ? 'var(--conf-low)' : undefined, dot: o.webhookFailures ? 'bad' : 'ok', onClick: () => setTab('events') },
            ]} />}
          </Block>
        </Cell>
        <Cell span={12}>
          <div className="blk" style={{ height: 'auto', padding: 0 }}>
            <div className="blk__tabs" role="tablist" style={{ borderTop: 0 }}>
              {TABS.map((t) => <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`blk__tab ${tab === t.key ? 'blk__tab--on' : ''}`} onClick={() => setTab(t.key)}>{t.label}{t.key === 'events' && o?.webhookFailures ? <span className="pill" style={{ padding: '1px 5px', borderRadius: 4, background: 'var(--conf-low-soft)', color: 'var(--conf-low)' }}>{o.webhookFailures}</span> : null}</button>)}
            </div>
          </div>
        </Cell>
        <Cell span={12}>
          {tab === 'plans' && <BillingPlans plans={plans} mobile={mobile} />}
          {tab === 'promo-codes' && <BillingPromoCodes mobile={mobile} paused={paused} />}
          {tab === 'settings' && <BillingSettings plans={plans} mobile={mobile} paused={paused} />}
          {tab === 'events' && <BillingEvents mobile={mobile} paused={paused} />}
        </Cell>
      </Grid>
    </>
  );
}
